import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { AdTopBanner, AdInContent, AdBottomBanner } from '../components/AdSense';

const CompoundInterestCalculatorPage = () => {
  const [principal, setPrincipal] = useState('10000');
  const [rate, setRate] = useState('5');
  const [time, setTime] = useState('10');
  const [timeUnit, setTimeUnit] = useState('years');
  const [compound, setCompound] = useState('monthly');
  const [contribution, setContribution] = useState('');
  const [contributionFreq, setContributionFreq] = useState('monthly');
  const [result, setResult] = useState(null);

  const compoundOptions = [
    { value: 'annually', label: 'Annually', n: 1 },
    { value: 'semiannually', label: 'Semi-annually', n: 2 },
    { value: 'quarterly', label: 'Quarterly', n: 4 },
    { value: 'monthly', label: 'Monthly', n: 12 },
    { value: 'daily', label: 'Daily', n: 365 }
  ];

  const calculate = () => {
    const P = parseFloat(principal) || 0;
    const r = (parseFloat(rate) || 0) / 100;
    const years = timeUnit === 'years' ? parseFloat(time) : parseFloat(time) / 12;
    const n = compoundOptions.find(c => c.value === compound)?.n || 12;
    const PMT = parseFloat(contribution) || 0;

    // A = P(1 + r/n)^(nt) + PMT * (((1 + r/n)^(nt) - 1) / (r/n))
    const compoundFactor = Math.pow(1 + r / n, n * years);
    const principalFV = P * compoundFactor;
    
    let contributionFV = 0;
    if (PMT > 0) {
      const periods = contributionFreq === 'monthly' ? years * 12 : years;
      const periodicRate = contributionFreq === 'monthly' ? r / 12 : r;
      contributionFV = PMT * ((Math.pow(1 + periodicRate, periods) - 1) / periodicRate);
    }

    const totalValue = principalFV + contributionFV;
    const totalContributions = P + (PMT * (contributionFreq === 'monthly' ? years * 12 : years));
    const totalInterest = totalValue - totalContributions;

    setResult({
      totalValue,
      totalInterest,
      totalContributions,
      principalFV,
      principalPercent: ((totalContributions / totalValue) * 100).toFixed(0),
      interestPercent: ((totalInterest / totalValue) * 100).toFixed(0)
    });
  };

  const formatCurrency = (num) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);

  return (
    <div className="min-h-screen bg-[#0d0d0d] flex flex-col">
      <Header />
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <AdTopBanner />
          
          <nav className="text-sm text-gray-500 mb-4">
            <Link to="/home" className="text-cyan-400 hover:text-cyan-300">Home</Link>
            <span className="mx-2">›</span>
            <span className="text-gray-400">Compound Interest Calculator</span>
          </nav>

          <h1 className="text-3xl font-bold text-white mb-4">Compound Interest Calculator</h1>
          <p className="text-gray-400 mb-6">
            Calculate how much your money can grow using the power of compound interest.
          </p>

          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-3">
                  <label className="text-gray-300 font-medium w-32">Principal</label>
                  <div className="flex items-center gap-1">
                    <span className="text-gray-400">$</span>
                    <Input type="number" value={principal} onChange={(e) => setPrincipal(e.target.value)}
                      className="w-32 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <label className="text-gray-300 font-medium w-32">Interest Rate</label>
                  <div className="flex items-center gap-1">
                    <Input type="number" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)}
                      className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                    <span className="text-gray-400">%</span>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <label className="text-gray-300 font-medium w-32">Time Period</label>
                  <div className="flex items-center gap-2">
                    <Input type="number" value={time} onChange={(e) => setTime(e.target.value)}
                      className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                    <select value={timeUnit} onChange={(e) => setTimeUnit(e.target.value)}
                      className="px-3 py-2 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-white text-sm">
                      <option value="years">Years</option>
                      <option value="months">Months</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-3">
                  <label className="text-gray-300 font-medium w-32">Compound</label>
                  <select value={compound} onChange={(e) => setCompound(e.target.value)}
                    className="px-3 py-2 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-white text-sm">
                    {compoundOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <label className="text-gray-300 font-medium w-32">Contribution</label>
                  <div className="flex items-center gap-1">
                    <span className="text-gray-400">$</span>
                    <Input type="number" value={contribution} onChange={(e) => setContribution(e.target.value)}
                      className="w-28 bg-[#0d0d0d] border-[#2a2a2a] text-white" placeholder="0" />
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <label className="text-gray-300 font-medium w-32">Contribution Freq.</label>
                  <select value={contributionFreq} onChange={(e) => setContributionFreq(e.target.value)}
                    className="px-3 py-2 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-white text-sm">
                    <option value="monthly">Monthly</option>
                    <option value="annually">Annually</option>
                  </select>
                </div>
              </div>
            </div>

            <Button onClick={calculate} className="mt-6 bg-cyan-600 hover:bg-cyan-500 text-white px-8">
              Calculate
            </Button>

            {result && (
              <div className="mt-6 p-5 bg-[#0d0d0d] border border-[#2a2a2a] rounded-xl">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <div className="flex justify-between p-3 bg-[#1a1a1a] rounded-lg">
                      <span className="text-gray-400">Future Value</span>
                      <span className="font-bold text-green-400 text-lg">{formatCurrency(result.totalValue)}</span>
                    </div>
                    <div className="flex justify-between p-3 bg-[#1a1a1a] rounded-lg">
                      <span className="text-gray-400">Total Interest</span>
                      <span className="font-semibold text-cyan-400">{formatCurrency(result.totalInterest)}</span>
                    </div>
                    <div className="flex justify-between p-3 bg-[#1a1a1a] rounded-lg">
                      <span className="text-gray-400">Total Contributions</span>
                      <span className="font-semibold text-white">{formatCurrency(result.totalContributions)}</span>
                    </div>
                  </div>
                  <div className="flex flex-col items-center justify-center">
                    <div className="relative w-32 h-32">
                      <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
                        <circle cx="50" cy="50" r="40" fill="transparent" stroke="#06b6d4" strokeWidth="20"
                          strokeDasharray={`${result.principalPercent * 2.51} 251`} />
                        <circle cx="50" cy="50" r="40" fill="transparent" stroke="#f59e0b" strokeWidth="20"
                          strokeDasharray={`${result.interestPercent * 2.51} 251`}
                          strokeDashoffset={`-${result.principalPercent * 2.51}`} />
                      </svg>
                    </div>
                    <div className="mt-4 text-xs space-y-1">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-cyan-500 rounded"></div>
                        <span className="text-gray-400">{result.principalPercent}% Contributions</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-amber-500 rounded"></div>
                        <span className="text-gray-400">{result.interestPercent}% Interest</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <AdInContent />

          <div className="mt-10 space-y-8">
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">What is Compound Interest?</h2>
              <p className="text-gray-400 leading-relaxed">
                Compound interest is interest calculated on the initial principal and also on the accumulated interest 
                from previous periods. It's often called "interest on interest" and will make a sum grow at a faster 
                rate than simple interest.
              </p>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Compound Interest Formula</h2>
              <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-4">
                <p className="text-gray-300 font-mono">A = P(1 + r/n)^(nt)</p>
                <p className="text-gray-500 text-sm mt-2">Where: A = final amount, P = principal, r = annual rate, n = compounds per year, t = years</p>
              </div>
            </section>
          </div>

          <div className="mt-10 p-6 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl">
            <h3 className="text-lg font-semibold text-white mb-4">Financial Calculators</h3>
            <div className="flex flex-wrap gap-2">
              {[{ name: 'Interest', path: '/interest-calculator' }, { name: 'Investment', path: '/investment-calculator' }, { name: 'Loan', path: '/loan-calculator' }, { name: 'Mortgage', path: '/mortgage-calculator' }].map((calc) => (
                <Link key={calc.path} to={calc.path} className="px-3 py-1.5 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-cyan-400 hover:text-cyan-300 text-sm">{calc.name}</Link>
              ))}
            </div>
          </div>
          <AdBottomBanner />
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CompoundInterestCalculatorPage;
